import React, { useState, useEffect } from 'react';
import { Clock, Monitor, Smartphone, Tablet } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import SessionDetailModal from './SessionDetailModal';

interface Session {
  id: number | string;
  visitorId?: string;
  userId?: number | null;
  sessionToken?: string;
  ipAddress: string;
  userAgent?: string;
  browserName: string;
  browserVersion: string;
  osName: string;
  osVersion: string;
  deviceType: string;
  isActive: boolean;
  lastActivity: string;
  sessionCreatedAt?: string;
  createdAt?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  visitCount?: number;
}

const SessionList: React.FC = () => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    userId: '',
    isActive: '',
    deviceType: ''
  });

  useEffect(() => {
    fetchSessions();
  }, [filters]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (filters.userId) params.userId = filters.userId;
      if (filters.isActive !== '') params.isActive = filters.isActive;
      if (filters.deviceType) params.deviceType = filters.deviceType;

      const response = await api.get('/admin/sessions', { params });
      if (response.data.success) {
        setSessions(response.data.sessions || []);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка загрузки сессий');
    } finally {
      setLoading(false);
    }
  };


  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType?.toLowerCase()) {
      case 'mobile':
        return <Smartphone className="h-4 w-4" />;
      case 'tablet':
        return <Tablet className="h-4 w-4" />;
      default:
        return <Monitor className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md">
      <div className="p-6 border-b border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Пользователь (ID)
            </label>
            <input
              type="text"
              value={filters.userId}
              onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
              placeholder="Фильтр по ID пользователя"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Статус
            </label>
            <select
              value={filters.isActive}
              onChange={(e) => setFilters({ ...filters, isActive: e.target.value })}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Все</option>
              <option value="true">Активные</option>
              <option value="false">Неактивные</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Тип устройства
            </label>
            <select
              value={filters.deviceType}
              onChange={(e) => setFilters({ ...filters, deviceType: e.target.value })}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Все</option>
              <option value="desktop">Desktop</option>
              <option value="mobile">Mobile</option>
              <option value="tablet">Tablet</option>
            </select>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Пользователь
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                IP адрес
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Устройство
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Браузер / ОС
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Последняя активность
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Статус
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sessions.map((session) => (
              <tr 
                key={session.id} 
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => {
                  setSelectedSession(session);
                  setIsModalOpen(true);
                }}
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {session.email ? (
                    <div>
                      <div className="font-medium text-gray-900">
                        {session.firstName} {session.lastName}
                      </div>
                      <div className="text-gray-500">{session.email}</div>
                    </div>
                  ) : (
                    <span className="text-gray-500">ID: {session.userId || 'Гость'}</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {session.ipAddress}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="flex items-center">
                    {getDeviceIcon(session.deviceType)}
                    <span className="ml-2 capitalize">{session.deviceType || 'desktop'}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div>{session.browserName} {session.browserVersion}</div>
                  <div className="text-xs text-gray-400">{session.osName} {session.osVersion}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    {new Date(session.lastActivity).toLocaleString('ru-RU')}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {session.isActive ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Активна
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Неактивна
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Модальное окно с детальной информацией о сессии */}
      {selectedSession && (
        <SessionDetailModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedSession(null);
          }}
          visitorId={selectedSession.visitorId}
          userId={selectedSession.userId || undefined}
          sessionInfo={{
            email: selectedSession.email,
            firstName: selectedSession.firstName,
            lastName: selectedSession.lastName,
            firstVisit: selectedSession.sessionCreatedAt || selectedSession.createdAt,
            lastActivity: selectedSession.lastActivity
          }}
        />
      )}
    </div>
  );
};

export default SessionList;

